// api.ts
import axios, { AxiosError } from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

// Choose the header your backend expects:
const AUTH_HEADER = "authorizationToken"; 
const USE_BEARER = false;                 

export const api = axios.create({
  baseURL: process.env.REACT_APP_ROOT_API ||   "http://localhost:3001",
  headers: { "Content-Type": "application/json" },
});

interface TokenCache {
  token: string;
  expiresAt: number; // Unix timestamp in ms
}

let tokenCache: TokenCache | null = null;
let fetchingPromise: Promise<string | null> | null = null; 

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

Hub.listen("auth", ({ payload }) => {
  const event = payload?.event;
  if (event === "signedOut" || event === "tokenRefresh_failure") {
    tokenCache = null;
    fetchingPromise = null;
  }
});

async function getAuthToken(): Promise<string | null> {
  const now = Date.now();

  if (tokenCache && tokenCache.expiresAt > now + REFRESH_BUFFER_MS) {
    return tokenCache.token;
  }

  if (fetchingPromise) {
    return fetchingPromise;
  }

  fetchingPromise = (async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (idToken) {
        const token = idToken.toString();
        const payload = idToken.payload;
        const expiresAt = (payload.exp as number) * 1000; 

        tokenCache = { token, expiresAt };
        return token;
      }

      return null;
    } catch (error) {
      tokenCache = null;
      return null;
    } finally {
      fetchingPromise = null;
    }
  })();

  return fetchingPromise;
}

export function clearAuthTokenCache() {
  tokenCache = null;
  fetchingPromise = null;
}

api.interceptors.request.use(
  async (config) => {
    try {
      const idToken = await getAuthToken();
      if (idToken) {
        const value = USE_BEARER ? `Bearer ${idToken}` : idToken;

        // Axios v1-safe header setting:
        if ((config.headers as any)?.set) {
          (config.headers as any).set(AUTH_HEADER, value);
        } else if (config.headers) {
          (config.headers as any)[AUTH_HEADER] = value;
        } else {
          config.headers = { [AUTH_HEADER]: value } as any;
        }
      }
    } catch {
      // no session; proceed without token
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const cfg: any = error.config || {};
    if ((status === 401 || status === 403) && !cfg._retry) {
      cfg._retry = true;

      // Clear the cache to force a fresh token fetch
      clearAuthTokenCache();

      try {
        const idToken = await getAuthToken();
        if (idToken) {
          const value = USE_BEARER ? `Bearer ${idToken}` : idToken;
          if ((cfg.headers as any)?.set) {
            (cfg.headers as any).set(AUTH_HEADER, value);
          } else if (cfg.headers) {
            (cfg.headers as any)[AUTH_HEADER] = value;
          } else {
            cfg.headers = { [AUTH_HEADER]: value };
          }
          return api(cfg); // retry once with fresh token
        }
      } catch {
        // fall through to reject
      }
    }
    return Promise.reject(error);
  }
);
